import {isPlatformBrowser, NgIf} from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    PLATFORM_ID,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import type {GeoJsonObject} from 'geojson';
import type {GeoJSON as LeafletGeoJSON, LatLngBounds, LayerGroup, Map as LeafletMap, MaplibreGL,} from 'leaflet';
import type {Map as MapLibreMap} from 'maplibre-gl';
import {RouteResult} from '../../../interfaces/route';

const BRIGHT_MAP_PALETTE = [
    ['background', 'background-color', '#f7f4e9'],
    ['park', 'fill-color', '#cfe8c8'],
    ['water', 'fill-color', '#b9def2'],
    ['landuse_residential', 'fill-color', '#eee8dc'],
    ['landcover_wood', 'fill-color', '#bdddb7'],
    ['waterway', 'line-color', '#8fc9e7'],
    ['building', 'fill-color', '#eadfce'],
    ['building', 'fill-outline-color', '#d4c3ac'],
    ['highway_minor', 'line-color', '#eadcbd'],
    ['highway_major_casing', 'line-color', '#d6b17c'],
    ['highway_major_inner', 'line-color', '#fff3cd'],
    ['highway_major_subtle', 'line-color', '#e0b982'],
    ['highway_motorway_casing', 'line-color', '#d9915c'],
    ['highway_motorway_inner', 'line-color', '#ffd09a'],
    ['highway_motorway_subtle', 'line-color', '#e6a56e'],
    ['tunnel_motorway_casing', 'line-color', '#d9ad82'],
    ['tunnel_motorway_inner', 'line-color', '#f7ddbd'],
    ['highway_motorway_bridge_casing', 'line-color', '#d9915c'],
    ['highway_motorway_bridge_inner', 'line-color', '#ffd09a'],
] as const;

@Component({
    selector: 'app-route-map',
    standalone: true,
    imports: [NgIf],
    templateUrl: './route-map.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RouteMapComponent implements AfterViewInit, OnChanges, OnDestroy {
    @Input({required: true}) result!: RouteResult;
    @ViewChild('mapCanvas') private mapCanvas?: ElementRef<HTMLDivElement>;

    mapReady = false;
    mapError = false;
    wheelZoomUnlocked = false;

    private viewReady = false;
    private renderRevision = 0;
    private leaflet?: typeof import('leaflet');
    private map?: LeafletMap;
    private routeLayer?: LeafletGeoJSON;
    private endpointLayer?: LayerGroup;
    private routeBounds?: LatLngBounds;
    private baseLayer?: MaplibreGL;
    private baseMapReady?: Promise<void>;
    private resizeObserver?: ResizeObserver;
    private resizeFrame?: number;

    constructor(
        private changeDetectorRef: ChangeDetectorRef,
        @Inject(PLATFORM_ID) private platformId: object,
    ) {
    }

    ngAfterViewInit(): void {
        this.viewReady = true;
        if (isPlatformBrowser(this.platformId) && this.mapCanvas?.nativeElement) {
            this.resizeObserver = new ResizeObserver(() => this.scheduleViewportSync());
            this.resizeObserver.observe(this.mapCanvas.nativeElement);
        }
        void this.renderMap();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['result'] && this.viewReady) {
            void this.renderMap();
        }
    }

    ngOnDestroy(): void {
        this.renderRevision++;
        this.resizeObserver?.disconnect();
        if (this.resizeFrame !== undefined) {
            window.cancelAnimationFrame(this.resizeFrame);
        }
        this.baseLayer?.off();
        this.map?.stop();
        this.map?.remove();
        this.map = undefined;
    }

    routeLabel(): string {
        return `Интерактивная карта маршрута ${this.result.origin.name} — ${this.result.destination.name}`;
    }

    private async renderMap(): Promise<void> {
        if (!isPlatformBrowser(this.platformId) || !this.viewReady || !this.result?.geometry) {
            return;
        }

        const revision = ++this.renderRevision;
        this.wheelZoomUnlocked = false;
        this.mapError = false;
        this.mapReady = false;
        this.disableWheelZoom();
        this.changeDetectorRef.markForCheck();

        try {
            const leafletImport = this.leaflet ?? await import('leaflet');
            const L = typeof leafletImport.map === 'function'
                ? leafletImport
                : (leafletImport as typeof leafletImport & { default?: typeof import('leaflet') }).default;
            if (!L || typeof L.map !== 'function') {
                throw new Error('Leaflet map factory is unavailable.');
            }
            if (revision !== this.renderRevision) {
                return;
            }
            this.leaflet = L;

            const element = this.mapCanvas?.nativeElement;
            if (!element?.isConnected) {
                return;
            }

            if (!this.map) {
                await import('@maplibre/maplibre-gl-leaflet');
                if (revision !== this.renderRevision) {
                    return;
                }
                const leafletModule = L as typeof import('leaflet') & { default?: typeof import('leaflet') };
                const createMapLibreLayer = leafletModule.maplibreGL ?? leafletModule.default?.maplibreGL;
                if (!createMapLibreLayer) {
                    throw new Error('MapLibre Leaflet integration is unavailable.');
                }

                this.map = L.map(element, {
                    attributionControl: false,
                    fadeAnimation: false,
                    keyboard: true,
                    markerZoomAnimation: false,
                    scrollWheelZoom: false,
                    zoomControl: true,
                    zoomAnimation: false,
                }).setView([this.result.origin.latitude, this.result.origin.longitude], 6);

                this.baseLayer = createMapLibreLayer({
                    attributionControl: false,
                    interactive: false,
                    style: new URL('assets/map/map-style-ru.json', document.baseURI).toString(),
                });
                this.baseLayer.addTo(this.map);
                const baseMap = this.baseLayer.getMaplibreMap();
                baseMap.once('load', () => this.scheduleViewportSync());
                this.baseMapReady = this.waitForBaseMap(baseMap);
            }

            this.map.stop();
            this.map.invalidateSize({animate: false, pan: false});
            this.routeLayer?.remove();
            this.endpointLayer?.remove();

            this.routeLayer = L.geoJSON(this.result.geometry as GeoJsonObject, {
                style: {
                    color: '#dc3545',
                    opacity: 0.96,
                    weight: 5,
                },
            }).addTo(this.map);

            this.endpointLayer = L.layerGroup().addTo(this.map);
            this.addEndpoint(this.result.origin.latitude, this.result.origin.longitude, this.result.origin.name, '#15803d');
            this.addEndpoint(this.result.destination.latitude, this.result.destination.longitude, this.result.destination.name, '#dc3545');

            this.routeBounds = this.routeLayer.getBounds();
            await this.baseMapReady;
            if (revision !== this.renderRevision) {
                return;
            }
            this.applyBrightPalette(this.baseLayer.getMaplibreMap());
            const layoutReady = await this.waitForStableLayout(revision);
            if (!layoutReady) {
                return;
            }
            this.syncViewport();

            this.mapReady = true;
            this.changeDetectorRef.markForCheck();
        } catch (error) {
            if (revision !== this.renderRevision) {
                return;
            }
            console.error('Route map initialization failed:', error);
            this.mapError = true;
            this.mapReady = false;
            this.changeDetectorRef.markForCheck();
        }
    }

    unlockWheelZoom(): void {
        if (!this.map || this.mapError || !this.mapReady) {
            return;
        }

        this.wheelZoomUnlocked = true;
        this.map.scrollWheelZoom.enable();
        this.changeDetectorRef.markForCheck();
    }

    private addEndpoint(latitude: number, longitude: number, title: string, color: string): void {
        if (!this.leaflet || !this.endpointLayer) {
            return;
        }

        this.leaflet.circleMarker([latitude, longitude], {
            color: '#ffffff',
            fillColor: color,
            fillOpacity: 1,
            radius: 8,
            weight: 3,
        })
            .bindTooltip(title, {direction: 'top', offset: [0, -8]})
            .addTo(this.endpointLayer);
    }

    private waitForStableLayout(revision: number): Promise<boolean> {
        return new Promise((resolve) => {
            window.requestAnimationFrame(() => {
                window.requestAnimationFrame(() => {
                    const element = this.mapCanvas?.nativeElement;
                    resolve(
                        revision === this.renderRevision &&
                        !!element?.isConnected &&
                        element.clientWidth > 0 &&
                        element.clientHeight > 0
                    );
                });
            });
        });
    }

    private applyBrightPalette(map: MapLibreMap): void {
        for (const [layerId, property, color] of BRIGHT_MAP_PALETTE) {
            if (map.getLayer(layerId)) {
                map.setPaintProperty(layerId, property, color);
            }
        }
    }

    private disableWheelZoom(): void {
        this.map?.scrollWheelZoom.disable();
    }

    private waitForBaseMap(map: MapLibreMap): Promise<void> {
        if (map.loaded()) {
            return Promise.resolve();
        }

        return new Promise((resolve, reject) => {
            let timeoutId = 0;
            const finish = (): void => {
                window.clearTimeout(timeoutId);
                map.off('load', finish);
                resolve();
            };
            timeoutId = window.setTimeout(() => {
                map.off('load', finish);
                reject(new Error('Russian vector map loading timed out.'));
            }, 12000);
            map.once('load', finish);
        });
    }

    private scheduleViewportSync(): void {
        if (!this.map || !this.routeBounds?.isValid()) {
            return;
        }
        if (this.resizeFrame !== undefined) {
            window.cancelAnimationFrame(this.resizeFrame);
        }
        this.resizeFrame = window.requestAnimationFrame(() => {
            this.resizeFrame = window.requestAnimationFrame(() => {
                this.resizeFrame = undefined;
                this.syncViewport();
            });
        });
    }

    private syncViewport(): void {
        if (!this.map || !this.routeBounds?.isValid()) {
            return;
        }
        this.map.invalidateSize({animate: false, pan: false});
        this.map.fitBounds(this.routeBounds.pad(0.08), {
            animate: false,
            maxZoom: 13,
            padding: [24, 24],
        });
    }
}
